import { Dialog, DropdownMenu, Flex, IconButton } from '@radix-ui/themes'
import { ChevronLeftIcon, ChevronRightIcon, HistoryIcon, XIcon } from 'lucide-react'
import confetti from 'canvas-confetti'
import { useEffect, useState } from 'react'

import '@/styles/announcement.css'

import { ANNOUNCEMENTS, LATEST_ANNOUNCEMENT } from '../data/announcements'
import { useMediaQuery } from '../hooks/useMediaQuery'

import type { Announcement } from '../entries/types'

interface UpdatesDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

// The width where AnnouncementBody splits into two columns (Tailwind `md`).
// Below it, an entry that ships a phone version shows that one instead.
const TWO_COLUMN_QUERY = '(min-width: 768px)'

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']

// Built from the parts rather than through Date: an ISO day parsed as UTC and
// rendered in Chile's offset lands on the previous day, which for the first of
// a month prints the wrong month entirely.
const formatMonth = (iso: string) => {
  const [year, month] = iso.split('-')

  return `${MONTHS[Number(month) - 1]} ${year}`
}

const releaseLabel = (entry: Announcement) =>
  entry.label ?? `Veekls Cloud v${entry.version} · ${formatMonth(entry.date)}`

interface NavArrowProps {
  direction: 'prev' | 'next'
  label: string
  disabled: boolean
  onClick: () => void
}

// The clickable region is the whole flank — from the card's edge out to the
// screen's edge — not just the chevron glyph inside it, which only signals
// which way it goes.
const NavArrow = ({ direction, label, disabled, onClick }: NavArrowProps) => (
  <button
    type="button"
    className="vk-nav-arrow hidden flex-1 items-center justify-center sm:flex"
    data-direction={direction}
    aria-label={label}
    disabled={disabled}
    onClick={onClick}
  >
    {direction === 'prev' ? <ChevronLeftIcon className="h-9 w-9" /> : <ChevronRightIcon className="h-9 w-9" />}
  </button>
)

const celebrate = () => {
  // Respect users who have asked the OS for less motion.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

  // Fire and forget: the burst is decorative, nothing waits on it.
  void confetti({
    particleCount: 120,
    spread: 80,
    origin: { y: 0.6 },
    colors: ['#107c41', '#e5484d', '#8b5cf6', '#1c1917'],
  })
}

export const UpdatesDialog = ({ open, onOpenChange }: UpdatesDialogProps) => {
  const [version, setVersion] = useState(LATEST_ANNOUNCEMENT.version)
  // Only set by the arrows, so a dropdown pick never inherits a stale flip
  // direction from whatever the arrows last did.
  const [flipDirection, setFlipDirection] = useState<'older' | 'newer'>()
  const twoColumns = useMediaQuery(TWO_COLUMN_QUERY)

  // Reopening always lands on the newest one. Someone who wandered into the
  // archive last time is not asking to start there next time.
  useEffect(() => {
    if (open) {
      setVersion(LATEST_ANNOUNCEMENT.version)
      setFlipDirection(undefined)
    }
  }, [open])

  const currentIndex = ANNOUNCEMENTS.findIndex((entry) => entry.version === version)
  const announcement = ANNOUNCEMENTS[currentIndex] ?? LATEST_ANNOUNCEMENT
  const isLatest = announcement.version === LATEST_ANNOUNCEMENT.version
  // Only one of the two is mounted, so only one journey's timers ever run.
  const showPhoneBody = !twoColumns && announcement.MobileBody !== undefined
  const Body = (showPhoneBody ? announcement.MobileBody : undefined) ?? announcement.Body

  const hasNewer = currentIndex > 0
  const hasOlder = currentIndex < ANNOUNCEMENTS.length - 1

  const goNewer = () => {
    if (!hasNewer) return
    setFlipDirection('newer')
    setVersion(ANNOUNCEMENTS[currentIndex - 1].version)
  }

  const goOlder = () => {
    if (!hasOlder) return
    setFlipDirection('older')
    setVersion(ANNOUNCEMENTS[currentIndex + 1].version)
  }

  const pickFromMenu = (entryVersion: string) => {
    setFlipDirection(undefined)
    setVersion(entryVersion)
  }

  const dismiss = () => {
    celebrate()
    onOpenChange(false)
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {/* Spans the full viewport and is stripped of its own chrome: the card
          below carries the visible surface, so the arrows can fill the rest
          of the screen on either side of it, all the way to its edges. */}
      <Dialog.Content
        maxWidth="none"
        width="100%"
        className="!overflow-visible !rounded-none !border-none !bg-transparent !p-0 !shadow-none"
      >
        {/* Title and description exist for assistive tech only: Radix renders them
            as a heading and a paragraph, so the visual layout cannot live inside
            them without producing invalid DOM nesting. */}
        <Dialog.Title className="sr-only">
          {isLatest ? 'Nueva versión disponible' : 'Novedades anteriores'}: {announcement.title}
        </Dialog.Title>
        <Dialog.Description className="sr-only">{announcement.summary}</Dialog.Description>

        <Flex align="center" gap="4">
          <NavArrow direction="prev" label="Novedad anterior" disabled={!hasNewer} onClick={goNewer} />

          {/* basis-[1100px], not flex-1: the arrows are the ones that should
              soak up whatever room is left over, not split it evenly with
              the card — that shrunk the card whenever there was more than
              1100px + arrows to go around. */}
          <div className="vk-updates-card min-w-0 shrink basis-[1100px] max-w-full overflow-hidden rounded-[var(--radius-4)] bg-[var(--color-panel-solid)] shadow-[var(--shadow-6)]">
            <Flex align="center" justify="between" className="border-b border-[#e4e1e4] bg-[#faf8f8] px-6 py-4">
              <Flex align="center" gap="3">
                <img src="/icons/icon-96.png" alt="Veekls" className="h-7 w-7" />
                <div>
                  <p className="text-sm font-bold uppercase tracking-tight text-[#1c1917]">
                    {isLatest ? (announcement.kicker ?? 'Nueva Versión Disponible') : 'Novedades anteriores'}
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-[#706d71]">
                    {releaseLabel(announcement)}
                  </p>
                </div>
              </Flex>

              <Flex align="center" gap="2">
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger>
                    <IconButton variant="soft" color="gray" aria-label="Novedades anteriores">
                      <HistoryIcon className="h-4 w-4" />
                    </IconButton>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content variant="soft" color="gray">
                    <DropdownMenu.Label>Novedades</DropdownMenu.Label>
                    {ANNOUNCEMENTS.map((entry) => (
                      <DropdownMenu.CheckboxItem
                        key={entry.version}
                        checked={entry.version === announcement.version}
                        onCheckedChange={() => pickFromMenu(entry.version)}
                      >
                        <span className="text-[13px]">{entry.title}</span>
                        <span className="ml-2 font-mono text-[10px] uppercase text-[#706d71]">
                          {entry.label ?? `v${entry.version} · ${formatMonth(entry.date)}`}
                        </span>
                      </DropdownMenu.CheckboxItem>
                    ))}
                  </DropdownMenu.Content>
                </DropdownMenu.Root>

                <IconButton variant="soft" color="gray" aria-label="Cerrar" onClick={dismiss}>
                  <XIcon className="h-4 w-4" />
                </IconButton>
              </Flex>
            </Flex>

            {/* Keyed on the version so switching entries remounts the body: each
                journey owns timers, and a half-finished loop must not bleed into
                the next announcement. The layout is part of the key for the same
                reason: rotating a phone across the breakpoint swaps journeys, and
                the new one starts from its first step. data-flip only carries an
                arrow-driven swap — a dropdown pick remounts with no animation. */}
            <div
              key={`${announcement.version}-${showPhoneBody ? 'phone' : 'wide'}`}
              data-flip={flipDirection}
              className="vk-flip-page"
            >
              <Body active={open} onDismiss={dismiss} />
            </div>
          </div>

          <NavArrow direction="next" label="Siguiente novedad" disabled={!hasOlder} onClick={goOlder} />
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}
